import './accordion-plus.scss'

import React, { CSSProperties, useImperativeHandle, useState } from 'react'

export interface AccordionPlusProps {
    config?: {
        transitionDelay?: number
        transitionDuration?: number
        isInitialOpen?: boolean
        isHeaderClickable?: boolean
    }
    subcomponents?: {
        header?: React.ReactElement
    }
    children?: React.ReactNode
}

export interface AccordionPlusHandles {
    open: VoidFunction
    close: VoidFunction
    toggle: VoidFunction
}

export const AccordionPlus = React.forwardRef((props: AccordionPlusProps, ref: React.Ref<AccordionPlusHandles>) => {
    const { config, subcomponents, children } = props

    const [isOpen, setIsOpen] = useState(config?.isInitialOpen ? true : false)
    const [isTransitioning, setIsTransitioning] = useState(false)
    const [isPreTransition, setIsPreTransition] = useState(false)
    const [contentHeight, setContentHeight] = useState<string>('0')

    const transitionDelay: number = config?.transitionDelay ? config.transitionDelay : 0
    const transitionDuration: number = config?.transitionDuration ? config.transitionDuration : 0.5

    const open = () => {
        if (!isOpen) {
            setContentHeight(getInnerHeight() || '0')
            setIsPreTransition(true)

            setTimeout(() => {
                setIsPreTransition(false)
                setIsTransitioning(true)
                setTimeout(() => {
                    setIsTransitioning(false)
                    setIsOpen(true)
                }, transitionDuration * 1000)
            }, transitionDelay * 1000)
        }
    }

    const close = () => {
        if (isOpen) {
            setContentHeight('0')
            setIsPreTransition(true)
            setTimeout(() => {
                setIsPreTransition(false)
                setIsTransitioning(true)
                setIsOpen(false)
                setTimeout(() => {
                    setIsTransitioning(false)
                }, transitionDuration * 1000)
            }, transitionDelay * 1000)
        }
    }

    const toggle = () => {
        isOpen ? close() : open()
    }
    useImperativeHandle(ref, () => {
        return {
            open,
            close,
            toggle,
        }
    })

    const header = subcomponents?.header

    const contentStyles: CSSProperties = {
        transitionDelay: transitionDelay + 's',
        transitionDuration: transitionDuration + 's',
        maxHeight: contentHeight,
    }
    const innerRef = React.useRef<HTMLDivElement>()

    const getInnerHeight = () => {
        if (!innerRef) return false
        if (!innerRef.current) return false
        return innerRef.current.clientHeight + 'px'
    }

    const handleHeaderClick = () => {
        if (config?.isHeaderClickable === false) return
        toggle()
    }

    const statusClasses: string = `${isTransitioning ? 'transitioning' : ''} ${
        isPreTransition ? 'pre-transition' : ''
    } ${isOpen ? 'open' : ''} ${!isTransitioning && !isOpen ? 'closed' : ''}`

    return (
        <div className={`accordion-plus ${statusClasses}`}>
            {header && (
                <div className={`accordion-plus__header`} onClick={handleHeaderClick}>
                    {header}
                </div>
            )}
            {children && (
                <div className={`accordion-plus__content`} style={contentStyles}>
                    {children && (
                        <div className={`accordion-plus__content-inner`} ref={innerRef}>
                            {children}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
})

export const AccordionPlusTest = () => {
    const accordionRef = React.useRef()
    console.log('accordionRef', accordionRef)

    const header = () => {
        return <div style={{ height: '30px', background: 'gray' }}>Header</div>
    }
    const props = {
        subcomponents: {
            header: header(),
        },
    }

    return (
        <AccordionPlus ref={accordionRef} {...props}>
            <div style={{ height: '400px', background: 'red' }}>Blah blah</div>
        </AccordionPlus>
    )
}

export default AccordionPlus
